import { useEffect, useState } from 'react';
import { unByKey } from 'ol/Observable';
import { getLayersNeedingZoom } from '../utils';
import { formatTitle } from '../../util/TitleHelper';

const MAX_NAMES_SHOWN = 3;

// Tells the user when checked layers (ArcGIS feature layers) are too detailed
// to load at the current zoom, instead of them just silently not appearing.
const ForestDataMapZoomNotice = ({ map }) => {
  const [layerNames, setLayerNames] = useState([]);
  // Layers can need different zooms; zooming to the closest one loads them all.
  const [targetResolution, setTargetResolution] = useState(null);

  useEffect(() => {
    if (!map) return undefined;
    const update = () => {
      const layers = getLayersNeedingZoom(map);
      const names = layers.map((layer) => formatTitle(layer.get('layer_name')));
      setTargetResolution(
        layers.length
          ? Math.min(...layers.map((l) => l.getSource().get('minResolutionToLoad')))
          : null
      );
      setLayerNames((prev) =>
        prev.join('\n') === names.join('\n') ? prev : names
      );
    };
    // moveend covers zooming; rendercomplete covers layers being added,
    // removed or toggled on/off.
    const keys = [map.on('moveend', update), map.on('rendercomplete', update)];
    update();
    return () => unByKey(keys);
  }, [map]);

  if (layerNames.length === 0) return null;

  const onZoomIn = () => {
    map.getView().animate({ resolution: targetResolution, duration: 400 });
  };

  const shown = layerNames.slice(0, MAX_NAMES_SHOWN).join(', ');
  const hidden = layerNames.length - MAX_NAMES_SHOWN;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        top: '10px',
        left: 'calc((100% - 350px) / 2)',
        transform: 'translateX(-50%)',
        maxWidth: 'min(480px, calc(100% - 350px - 100px))',
        backgroundColor: '#2C3E37',
        color: 'white',
        border: '3px solid rgba(255, 255, 255, 0.72)',
        borderRadius: '6px',
        padding: '6px 10px',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        zIndex: 10,
      }}
    >
      <span>
        <strong>
          Zoom in to load{' '}
          {layerNames.length === 1 ? 'this layer' : `${layerNames.length} layers`}
          :
        </strong>{' '}
        {shown}
        {hidden > 0 && `, and ${hidden} more`}
      </span>
      <button
        onClick={onZoomIn}
        style={{
          flexShrink: 0,
          backgroundColor: 'white',
          color: '#2C3E37',
          border: 'none',
          borderRadius: '4px',
          padding: '4px 10px',
          fontWeight: 600,
          cursor: 'pointer',
          minHeight: '24px',
        }}
      >
        Zoom in
      </button>
    </div>
  );
};

export default ForestDataMapZoomNotice;
